import type {
  AssignmentLine,
  CartAssignment,
} from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { isSoLine } from '../components/cartLineColumns';
import {
  getLineDesignation,
  isPotfLine,
  selectCartPoLines,
} from './lineFilters';
import { isReleasedStatus } from './releasedStatus';
import { computeLineTotals, type LineTotals } from './lineTotals';

// Buy-vs-transfer view: 'po' hides pure-transfer rows, 'to' hides buy /
// buy+transfer rows, 'all' shows everything. Mirrors the board's top toggle.
export type DesignationFilter = 'all' | 'po' | 'to';

// Pre-flight summary of what a per-cart "Create orders" will mint, shown in the
// confirm dialog. A cart yields at most one PO (this supplier × ship-to); TOs
// are estimated by distinct transfer destination. `grandTotal` is the PO value.
export interface CartCreateSummary {
  poCount: number;
  toCount: number;
  grandTotal: number;
  // Demand coverage over the selected lines: distinct sales orders satisfied
  // and the number of deficit (min/max) lines addressed.
  salesOrderCount: number;
  deficitCount: number;
  // Distinct products (by id) on the buy vs transfer side. A buy+transfer line
  // counts on both sides.
  buySkuCount: number;
  transferSkuCount: number;
  // Cart's vendor thresholds so the dialog can render MOV/freight progress for
  // the PO value (`grandTotal`). Null when the vendor has no such threshold.
  movThreshold: number | null;
  freightThreshold: number | null;
  // The buy lines' items and quantities, for the freight the branch's rules
  // suggest at release (their item weights).
  buyLines: { productV3Key: string | null; quantity: number }[];
}

export interface CartStat {
  total: number;
  soCount: number;
  potfCount: number;
  /**
   * PO value of the cart — see {@link selectCartPoLines}. Named for the
   * quantity it holds, not "cost", because it is compared against the VENDOR
   * MOV/freight thresholds and a transfer contributes nothing to those.
   */
  poValue: number;
}

/** A cart with no lines yet. */
export const EMPTY_CART_STAT: CartStat = {
  total: 0,
  soCount: 0,
  potfCount: 0,
  poValue: 0,
};

export interface FilterOption {
  id: string;
  label: string;
}

export interface CartCreatePlan {
  summary: CartCreateSummary;
  hasCreatable: boolean;
  /** The in-cart lines of this cart: exactly what Release sends. */
  releaseSuggestionIds: string[];
  /** Totals of those same lines, whatever the table is filtered to. */
  approved: LineTotals;
}

// Stable per-product identity for distinct-SKU counts. productSku carries the
// ProductV3 key (which IS the SKU); suggestionId is the last resort so
// unidentifiable lines still count as distinct rather than collapsing together.
function skuIdentity(line: AssignmentLine): string {
  return line.productSku ?? line.suggestionId;
}

/**
 * 'po' keeps buy / buy+transfer lines and the lines a buyer switched from the
 * vendor to a transfer, which stay in the vendor cart (GEN-3023); 'to' keeps
 * the other pure transfers.
 */
export function filterByDesignation(
  lines: AssignmentLine[],
  designationFilter: DesignationFilter,
): AssignmentLine[] {
  if (designationFilter === 'all') return lines;
  return lines.filter((l) => {
    const d = getLineDesignation(l);
    if (designationFilter === 'to') {
      return d === 'transfer' && !l.isBuyerSwitchedTransfer;
    }
    return d === 'buy' || d === 'buy_and_transfer' || l.isBuyerSwitchedTransfer;
  });
}

export function dedupeCarts(carts: CartAssignment[]): CartAssignment[] {
  const seen = new Set<string>();
  return carts.filter((c) => {
    if (seen.has(c.shipToWarehouseId)) return false;
    seen.add(c.shipToWarehouseId);
    return true;
  });
}

export function indexCartsByShipTo(
  carts: CartAssignment[],
): Map<string, CartAssignment> {
  const map = new Map<string, CartAssignment>();
  for (const c of carts) map.set(c.shipToWarehouseId, c);
  return map;
}

/** Lines grouped by their cart (ship-to); every cart gets an entry. */
export function groupLinesByCart(
  carts: CartAssignment[],
  lines: AssignmentLine[],
): Map<string, AssignmentLine[]> {
  const map = new Map<string, AssignmentLine[]>();
  for (const cart of carts) map.set(cart.shipToWarehouseId, []);
  for (const line of lines) {
    if (line.shipToWarehouseId == null) continue;
    map.get(line.shipToWarehouseId)?.push(line);
  }
  return map;
}

/**
 * Ship-tos where at least one unreleased line's effective option is a
 * forward-buy: the uplifts Remove Forward Buy can still take back.
 */
export function forwardBoughtShipToSet(lines: AssignmentLine[]): Set<string> {
  const shipTos = new Set<string>();
  for (const line of lines) {
    if (isReleasedStatus(line.status)) continue;
    if (line.shipToWarehouseId != null && line.topOption?.forwardBuy != null) {
      shipTos.add(line.shipToWarehouseId);
    }
  }
  return shipTos;
}

function statForCart(
  cartLines: AssignmentLine[],
  selectedIds: Set<string>,
): CartStat {
  // Money comes from the shared definition so the rail and the create dialog
  // can never disagree; the counts deliberately stay over ALL cart lines.
  const { poValue } = selectCartPoLines(cartLines, selectedIds);
  return {
    total: cartLines.length,
    soCount: cartLines.filter(isSoLine).length,
    potfCount: cartLines.filter(isPotfLine).length,
    poValue,
  };
}

export function computeCartStats(
  carts: CartAssignment[],
  linesByCart: Map<string, AssignmentLine[]>,
  selectedIds: Set<string>,
): Map<string, CartStat> {
  const stats = new Map<string, CartStat>();
  for (const cart of carts) {
    stats.set(
      cart.shipToWarehouseId,
      statForCart(linesByCart.get(cart.shipToWarehouseId) ?? [], selectedIds),
    );
  }
  return stats;
}

function railSortKey(
  cart: CartAssignment,
  cartStats: Map<string, CartStat>,
): [number, number] {
  const stat = cartStats.get(cart.shipToWarehouseId);
  return [stat?.soCount ?? 0, stat?.poValue ?? 0];
}

/** Carts with at least one line, SO count desc then PO value desc. */
export function sortRailCarts(
  carts: CartAssignment[],
  cartStats: Map<string, CartStat>,
): CartAssignment[] {
  return carts
    .filter((c) => (cartStats.get(c.shipToWarehouseId)?.total ?? 0) > 0)
    .sort((a, b) => {
      const [soA, valueA] = railSortKey(a, cartStats);
      const [soB, valueB] = railSortKey(b, cartStats);
      return soB - soA || valueB - valueA;
    });
}

// Demand coverage: distinct sales orders (fall back to SO line count when a
// line carries no order id) and deficit (min/max) line count.
function demandCoverage(selectedLines: AssignmentLine[]): {
  salesOrderCount: number;
  deficitCount: number;
} {
  const salesOrderIds = new Set<string>();
  let soLineCount = 0;
  let deficitCount = 0;
  for (const l of selectedLines) {
    if (l.trigger === 'sales_order') {
      soLineCount += 1;
      if (l.triggerSalesOrderId) salesOrderIds.add(l.triggerSalesOrderId);
    } else if (l.trigger === 'deficit') {
      deficitCount += 1;
    }
  }
  return {
    salesOrderCount: salesOrderIds.size > 0 ? salesOrderIds.size : soLineCount,
    deficitCount,
  };
}

/** Per-cart create summary over its selected (non-trashed) lines. */
export function buildCartCreatePlan(
  cartLines: AssignmentLine[],
  selectedIds: Set<string>,
  cart: CartAssignment | undefined,
): CartCreatePlan {
  const {
    selected: selectedLines,
    buy: buyLines,
    transfer: transferLines,
    poValue,
  } = selectCartPoLines(cartLines, selectedIds);
  const transferDestIds = new Set(
    transferLines.map((l) => l.destinationWarehouseId),
  );
  return {
    summary: {
      poCount: buyLines.length > 0 ? 1 : 0,
      toCount: transferDestIds.size,
      grandTotal: poValue,
      ...demandCoverage(selectedLines),
      // productId is null on a v3-only suggestion, so keying the distinct
      // count on it would collapse every such line into one bucket. The SKU
      // is the stable product identity across v2 and v3.
      buySkuCount: new Set(buyLines.map(skuIdentity)).size,
      transferSkuCount: new Set(transferLines.map(skuIdentity)).size,
      movThreshold: cart?.movThreshold ?? null,
      freightThreshold: cart?.freightThreshold ?? null,
      buyLines: buyLines.map((l) => ({
        productV3Key: l.productV3Key,
        quantity: l.topOption?.buyQuantity ?? 0,
      })),
    },
    hasCreatable: selectedLines.length > 0,
    releaseSuggestionIds: selectedLines.map((l) => l.suggestionId),
    approved: computeLineTotals(cartLines, selectedIds),
  };
}

export function shipToOptions(
  lines: AssignmentLine[],
  cartsByShipTo: Map<string, CartAssignment>,
): FilterOption[] {
  const byId = new Map<string, string>();
  for (const l of lines) {
    if (l.shipToWarehouseId && !byId.has(l.shipToWarehouseId)) {
      const name =
        cartsByShipTo.get(l.shipToWarehouseId)?.shipToWarehouseName ??
        l.destinationWarehouseName;
      byId.set(l.shipToWarehouseId, name);
    }
  }
  return [...byId].map(([id, label]) => ({ id, label }));
}

export function warehouseOptions(lines: AssignmentLine[]): FilterOption[] {
  const byId = new Map<string, string>();
  for (const l of lines) {
    if (!byId.has(l.destinationWarehouseId)) {
      byId.set(l.destinationWarehouseId, l.destinationWarehouseName);
    }
  }
  return [...byId].map(([id, label]) => ({ id, label }));
}

export function hubOptions(lines: AssignmentLine[]): FilterOption[] {
  const hubs = new Set<string>();
  for (const l of lines) {
    if (l.destinationWarehouseHubName) hubs.add(l.destinationWarehouseHubName);
  }
  return [...hubs].map((h) => ({ id: h, label: h }));
}

export interface FocusFilters {
  selectedShipTo: string | null;
  shipToIds: Set<string>;
  warehouseIds: Set<string>;
  hubNames: Set<string>;
}

// An empty picker set means "no filter"; otherwise the value must be in it.
function passesPicker(picked: Set<string>, value: string | null): boolean {
  return picked.size === 0 || (value != null && picked.has(value));
}

/** Rail ship-to scope, then the ship-to / warehouse / hub focus pickers. */
export function applyFocusFilters(
  lines: AssignmentLine[],
  { selectedShipTo, shipToIds, warehouseIds, hubNames }: FocusFilters,
): AssignmentLine[] {
  return lines.filter(
    (l) =>
      (!selectedShipTo || l.shipToWarehouseId === selectedShipTo) &&
      passesPicker(shipToIds, l.shipToWarehouseId) &&
      passesPicker(warehouseIds, l.destinationWarehouseId) &&
      passesPicker(hubNames, l.destinationWarehouseHubName),
  );
}

// SO lines first (0), then transfer quantity desc.
function transferSortKey(line: AssignmentLine): [number, number] {
  return [isSoLine(line) ? 0 : 1, -(line.topOption?.transferQuantity ?? 0)];
}

/** Transfer carts: SO lines first, then transfer quantity desc. */
export function groupTransferLinesByCart(
  carts: CartAssignment[],
  lines: AssignmentLine[],
): Map<string, AssignmentLine[]> {
  const map = groupLinesByCart(carts, lines);
  for (const arr of map.values()) {
    arr.sort((a, b) => {
      const [soA, qtyA] = transferSortKey(a);
      const [soB, qtyB] = transferSortKey(b);
      return soA - soB || qtyA - qtyB;
    });
  }
  return map;
}
